import { useEffect, useMemo, useState } from "react";

import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, Text, TextInput } from "components";
import { DashboardService } from "services";
import {
  DashboardKpiMeterResponse,
  DashboardKpiParams,
  DashboardKpiPeriod,
  DashboardScope,
} from "types";

import SemiGauge from "../shared/SemiGauge";

import classes from "./OperativeSection.module.css";

interface OperativeSectionProps {
  className?: string;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onPeriodChange: (value: DashboardKpiPeriod) => void;
  params: DashboardKpiParams | null;
  period: DashboardKpiPeriod;
  scope?: DashboardScope;
}

interface OperativeMetric {
  labelKey: string;
  type: string;
  color: string;
}

interface OperativeMetricValue extends OperativeMetric {
  value: number;
  target: number;
}

const OPERATIVE_METRICS: readonly OperativeMetric[] = [
  {
    labelKey: "dashboard.admin.operative.metrics.qualifications",
    type: "QUALIFICATION",
    color: "#be4647",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.requests",
    type: "WEB_APP_REQUEST",
    color: "#69c34d",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.leadsInBank",
    type: "BANK_LEAD",
    color: "#eadf53",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.approved",
    type: "APPROVED",
    color: "#be4647",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.disbursed",
    type: "DISBURSED",
    color: "#eadf53",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.smallWins",
    type: "SMALL_WIN",
    color: "#be4647",
  },
] as const;

const createEmptyOperativeMetrics = (): OperativeMetricValue[] =>
  OPERATIVE_METRICS.map((metric) => ({
    ...metric,
    value: 0,
    target: 0,
  }));

const mapKpiMetersToMetrics = (
  metrics: readonly OperativeMetric[],
  meters: DashboardKpiMeterResponse[],
): OperativeMetricValue[] => {
  const metersByType = meters.reduce<Record<string, DashboardKpiMeterResponse>>((acc, item) => {
    acc[item.type] = item;
    return acc;
  }, {});

  return metrics.map((metric) => ({
    ...metric,
    value: typeof metersByType[metric.type]?.value === "number" ? metersByType[metric.type].value : 0,
    target: typeof metersByType[metric.type]?.target === "number" ? metersByType[metric.type].target : 0,
  }));
};

const OperativeGauge = ({ labelKey, value, target, color }: OperativeMetricValue) => {
  const { t } = useTranslation();

  return (
    <div className={classes.metric}>
      <SemiGauge
        size="sm"
        value={value}
        target={target}
        color={color}
        className={classes.gauge}
      />

      <Text className={classes.metricLabel}>{t(labelKey)}</Text>
      <Text className={classes.metricValue}>{`${value}/${target}`}</Text>
    </div>
  );
};

const OperativeSection = ({
  className,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onPeriodChange,
  params,
  period,
  scope = "admin",
}: OperativeSectionProps) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<OperativeMetricValue[]>(createEmptyOperativeMetrics());
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const periodOptions = useMemo(
    () => [
      { value: "DAILY", label: t("dashboard.admin.operative.period.options.daily") },
      { value: "WEEKLY", label: t("dashboard.admin.operative.period.options.weekly") },
      { value: "BIWEEKLY", label: t("dashboard.admin.operative.period.options.biweekly") },
      { value: "MONTHLY", label: t("dashboard.admin.operative.period.options.monthly") },
      { value: "QUARTERLY", label: t("dashboard.admin.operative.period.options.quarterly") },
      { value: "YEARLY", label: t("dashboard.admin.operative.period.options.yearly") },
      { value: "CUSTOM", label: t("dashboard.admin.operative.period.options.timeRange") },
    ],
    [t],
  );

  const isCustomPeriod = period === "CUSTOM";

  const handleDownloadReport = async () => {
    if (!params || isDownloadingReport) {
      return;
    }

    setIsDownloadingReport(true);

    try {
      await DashboardService.downloadAdminReport(params);
    } finally {
      setIsDownloadingReport(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (!params) {
      return () => {
        mounted = false;
      };
    }

    const loadKpis = async () => {
      try {
        const data = await DashboardService.getKpis(params, scope);

        if (!mounted) {
          return;
        }

        setMetrics(mapKpiMetersToMetrics(OPERATIVE_METRICS, data?.meters ?? []));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics(createEmptyOperativeMetrics());
      }
    };

    loadKpis();

    return () => {
      mounted = false;
    };
  }, [params, scope]);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      <div className={classes.header}>
        <div className={classes.headerMain}>
          <Text className={classes.title}>{t("dashboard.admin.operative.title")}</Text>

          <div className={classes.controls}>
            <select
              className={classes.select}
              value={period}
              onChange={(event) => onPeriodChange((event.currentTarget.value as DashboardKpiPeriod) ?? "YEARLY")}
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {isCustomPeriod ? (
              <div className={classes.dateRange}>
                <Text className={classes.dateLabel}>{t("dashboard.admin.operative.period.from")}</Text>
                <TextInput
                  className={classes.dateInput}
                  placeholder={t("dashboard.admin.operative.period.datePlaceholder")}
                  value={dateFrom}
                  onChange={(event) => onDateFromChange(event.currentTarget.value)}
                  mb={0}
                />

                <Text className={classes.dateLabel}>{t("dashboard.admin.operative.period.to")}</Text>
                <TextInput
                  className={classes.dateInput}
                  placeholder={t("dashboard.admin.operative.period.datePlaceholder")}
                  value={dateTo}
                  onChange={(event) => onDateToChange(event.currentTarget.value)}
                  mb={0}
                />
              </div>
            ) : null}
          </div>
        </div>

        {scope === "admin" ? (
          <Button
            type="button"
            className={classes.reportButton}
            variant="filled"
            radius="md"
            size="compact-sm"
            fz={13}
            leftSection={<IconDownload size={14} stroke={1.9} />}
            onClick={handleDownloadReport}
            loading={isDownloadingReport}
            disabled={!params}
          >
            {t("dashboard.admin.operative.downloadReport")}
          </Button>
        ) : null}
      </div>

      <div className={classes.metrics}>
        {metrics.map((metric) => (
          <OperativeGauge key={metric.labelKey} {...metric} />
        ))}
      </div>
    </section>
  );
};

export default OperativeSection;
